/* eslint-disable */
import { useState } from 'react';
import { signUp, signIn, signOut, updateProfile } from './supabaseClient';

// ── Shared styles ─────────────────────────────────────────────────────────────
var inp = {
  width: '100%',
  background: 'var(--c-mist)',
  border: '1px solid var(--c-line)',
  borderRadius: 'var(--r-md)',
  padding: '10px 14px',
  fontFamily: 'var(--font-ui)',
  fontSize: 'var(--fs-14)',
  color: 'var(--c-fg-1)',
  outline: 'none',
  transition: 'border-color var(--dur-1)',
};

var inpFocus = { borderColor: 'var(--c-water)', boxShadow: '0 0 0 2px rgba(59,130,232,0.15)' };

var btn = {
  width: '100%',
  padding: '11px',
  borderRadius: 'var(--r-md)',
  fontSize: 'var(--fs-14)',
  fontWeight: 700,
  fontFamily: 'var(--font-ui)',
  cursor: 'pointer',
  border: 'none',
  transition: 'all var(--dur-1) var(--ease-smooth)',
};

var btnPrimary = Object.assign({}, btn, {
  background: 'var(--c-water)',
  color: '#fff',
});

var btnGhost = Object.assign({}, btn, {
  background: 'transparent',
  border: '1px solid var(--c-line)',
  color: 'var(--c-fg-2)',
});

// ── Input with focus state ────────────────────────────────────────────────────
function Input({ label, type, value, onChange, placeholder, error, autoComplete }) {
  var [focused, setFocused] = useState(false);
  return (
    <div style={{ marginBottom: 16 }}>
      {label && (
        <label style={{ display: 'block', fontSize: 'var(--fs-13)', fontWeight: 600, color: 'var(--c-fg-2)', marginBottom: 6 }}>
          {label}
        </label>
      )}
      <input
        type={type || 'text'}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        autoComplete={autoComplete}
        onFocus={function() { setFocused(true); }}
        onBlur={function() { setFocused(false); }}
        style={Object.assign({}, inp, focused ? inpFocus : {}, error ? { borderColor: 'var(--c-fire)' } : {})}
      />
      {error && (
        <div style={{ fontSize: 'var(--fs-12)', color: 'var(--c-fire-ink)', marginTop: 4 }}>{error}</div>
      )}
    </div>
  );
}

// ── Password strength meter ───────────────────────────────────────────────────
function PasswordStrength({ password }) {
  if (!password) return null;
  var score = 0;
  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;

  var label = ['Very weak', 'Weak', 'Fair', 'Good', 'Strong'][score - 1] || 'Very weak';
  var color = ['var(--c-fire)', 'var(--c-fire)', 'var(--c-electric)', 'var(--c-grass)', 'var(--c-grass)'][score - 1] || 'var(--c-fire)';

  return (
    <div style={{ marginTop: -8, marginBottom: 16 }}>
      <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
        {[1, 2, 3, 4, 5].map(function(i) {
          return (
            <div key={i} style={{
              flex: 1, height: 3, borderRadius: 99,
              background: i <= score ? color : 'var(--c-line)',
              transition: 'background var(--dur-2)',
            }} />
          );
        })}
      </div>
      <div style={{ fontSize: 'var(--fs-11)', color: color, fontWeight: 600 }}>{label}</div>
    </div>
  );
}

// ── Auth Modal ────────────────────────────────────────────────────────────────
export function AuthModal({ onClose, onSuccess }) {
  var [mode, setMode] = useState('signin'); // 'signin' | 'signup' | 'verify'
  var [email, setEmail] = useState('');
  var [password, setPassword] = useState('');
  var [confirmPassword, setConfirmPassword] = useState('');
  var [loading, setLoading] = useState(false);
  var [error, setError] = useState('');
  var [fieldErrors, setFieldErrors] = useState({});

  function validate() {
    var errs = {};
    if (!email.trim()) errs.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errs.email = 'Enter a valid email';
    if (!password) errs.password = 'Password is required';
    if (mode === 'signup') {
      if (password.length < 8) errs.password = 'Password must be at least 8 characters';
      if (password !== confirmPassword) errs.confirmPassword = "Passwords don't match";
    }
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (!validate()) return;
    setLoading(true);
    try {
      if (mode === 'signup') {
        await signUp(email, password);
        setMode('verify');
      } else {
        var result = await signIn(email, password);
        onSuccess(result.session);
        onClose();
      }
    } catch (err) {
      // Map Supabase error messages to friendly ones
      var msg = err.message || 'Something went wrong';
      if (msg.includes('Invalid login')) msg = 'Incorrect email or password.';
      if (msg.includes('already registered')) msg = 'An account with this email already exists. Try signing in.';
      if (msg.includes('Email not confirmed')) msg = 'Please check your email and verify your account first.';
      setError(msg);
    }
    setLoading(false);
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(10px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
        animation: 'fade-in var(--dur-2) var(--ease-smooth)',
      }}>
      <div
        onClick={function(e) { e.stopPropagation(); }}
        style={{
          background: 'var(--c-bg-elevated)',
          border: '1px solid var(--c-line)',
          borderRadius: 'var(--r-xl)',
          padding: 32,
          width: '100%', maxWidth: 400,
          boxShadow: 'var(--sh-3)',
          animation: 'modal-in var(--dur-2) var(--ease-pop)',
        }}>

        {/* Header */}
        <div style={{ marginBottom: 24, textAlign: 'center' }}>
          <div style={{ fontFamily: "'Archivo', sans-serif", fontWeight: 900, fontSize: 'var(--fs-20)', color: 'var(--c-ink-pure)', letterSpacing: '-0.02em', marginBottom: 4 }}>
            Pokemania
          </div>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 'var(--fs-24)', color: 'var(--c-fg-1)', marginBottom: 4 }}>
            {mode === 'verify' ? 'Check your inbox' : mode === 'signin' ? 'Welcome back' : 'Create account'}
          </div>
          <div style={{ fontSize: 'var(--fs-14)', color: 'var(--c-fg-3)' }}>
            {mode === 'verify'
              ? 'We sent a verification link to ' + email
              : mode === 'signin'
              ? 'Sign in to access your decks'
              : 'Save your decks, track your progress'}
          </div>
        </div>

        {/* Verify screen */}
        {mode === 'verify' && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>📬</div>
            <p style={{ fontSize: 'var(--fs-14)', color: 'var(--c-fg-2)', lineHeight: 'var(--lh-normal)', marginBottom: 24 }}>
              Click the link in your email to verify your account, then come back and sign in.
            </p>
            <button onClick={function() { setMode('signin'); }} style={btnPrimary}>
              Go to sign in
            </button>
          </div>
        )}

        {/* Form */}
        {mode !== 'verify' && (
          <form onSubmit={handleSubmit}>
            <Input
              label="Email"
              type="email"
              value={email}
              onChange={function(e) { setEmail(e.target.value); setFieldErrors({}); }}
              placeholder="you@example.com"
              autoComplete="email"
              error={fieldErrors.email}
            />
            <Input
              label="Password"
              type="password"
              value={password}
              onChange={function(e) { setPassword(e.target.value); setFieldErrors({}); }}
              placeholder={mode === 'signup' ? 'At least 8 characters' : '••••••••'}
              autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
              error={fieldErrors.password}
            />
            {mode === 'signup' && <PasswordStrength password={password} />}
            {mode === 'signup' && (
              <Input
                label="Confirm password"
                type="password"
                value={confirmPassword}
                onChange={function(e) { setConfirmPassword(e.target.value); setFieldErrors({}); }}
                placeholder="••••••••"
                autoComplete="new-password"
                error={fieldErrors.confirmPassword}
              />
            )}

            {error && (
              <div style={{
                background: 'var(--c-fire-bg)', border: '1px solid var(--c-fire)',
                borderRadius: 'var(--r-md)', padding: '10px 14px',
                fontSize: 'var(--fs-13)', color: 'var(--c-fire-ink)',
                marginBottom: 16,
              }}>
                ⚠️ {error}
              </div>
            )}

            <button type="submit" disabled={loading} style={Object.assign({}, btnPrimary, { opacity: loading ? 0.6 : 1, marginBottom: 12 })}>
              {loading ? '...' : mode === 'signin' ? 'Sign in' : 'Create account'}
            </button>

            <button type="button" onClick={function() { setMode(mode === 'signin' ? 'signup' : 'signin'); setError(''); setFieldErrors({}); }} style={btnGhost}>
              {mode === 'signin' ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
            </button>
          </form>
        )}

        {/* Security note */}
        {mode === 'signup' && (
          <p style={{ fontSize: 'var(--fs-11)', color: 'var(--c-fg-3)', textAlign: 'center', marginTop: 16, lineHeight: 'var(--lh-normal)' }}>
            🔒 Passwords are hashed and never stored in plain text. Secured by Supabase.
          </p>
        )}
      </div>
    </div>
  );
}

// ── User menu (shown when logged in) ─────────────────────────────────────────
export function UserMenu({ session, profile, onSignOut, onProfileUpdate }) {
  var [open, setOpen] = useState(false);
  var [editingName, setEditingName] = useState(false);
  var [displayName, setDisplayName] = useState(profile?.display_name || '');
  var [saving, setSaving] = useState(false);

  var initials = (profile?.display_name || session?.user?.email || 'U')
    .slice(0, 2).toUpperCase();

  var avatarColor = profile?.avatar_color || 'var(--c-water)';

  async function handleSaveName() {
    if (!displayName.trim()) return;
    setSaving(true);
    try {
      await updateProfile(session.user.id, { display_name: displayName.trim() });
      if (onProfileUpdate) onProfileUpdate({ display_name: displayName.trim() });
      setEditingName(false);
    } catch (e) {
      console.error(e);
    }
    setSaving(false);
  }

  return (
    <div style={{ position: 'relative', flexShrink: 0 }}>
      <button
        onClick={function() { setOpen(function(o) { return !o; }); }}
        style={{
          width: 34, height: 34, borderRadius: 'var(--r-full)',
          background: avatarColor, color: '#fff',
          border: 'none', cursor: 'pointer',
          fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 'var(--fs-13)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
        {initials}
      </button>

      {open && (
        <>
          {/* Backdrop */}
          <div onClick={function() { setOpen(false); }} style={{ position: 'fixed', inset: 0, zIndex: 90 }} />
          {/* Dropdown */}
          <div style={{
            position: 'absolute', top: 42, right: 0, zIndex: 100,
            background: 'var(--c-bg-elevated)', border: '1px solid var(--c-line)',
            borderRadius: 'var(--r-lg)', boxShadow: 'var(--sh-3)',
            minWidth: 220, overflow: 'hidden',
            animation: 'modal-in var(--dur-1) var(--ease-pop)',
          }}>
            {/* Profile header */}
            <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--c-line)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                <div style={{ width: 36, height: 36, borderRadius: 'var(--r-full)', background: avatarColor, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 'var(--fs-14)', flexShrink: 0 }}>
                  {initials}
                </div>
                <div style={{ minWidth: 0 }}>
                  {editingName ? (
                    <div style={{ display: 'flex', gap: 6 }}>
                      <input
                        value={displayName}
                        onChange={function(e) { setDisplayName(e.target.value); }}
                        onKeyDown={function(e) { if (e.key === 'Enter') handleSaveName(); if (e.key === 'Escape') setEditingName(false); }}
                        autoFocus
                        style={Object.assign({}, inp, { padding: '4px 8px', fontSize: 'var(--fs-13)', width: '100%' })}
                      />
                      <button onClick={handleSaveName} disabled={saving} style={{ padding: '4px 8px', borderRadius: 'var(--r-sm)', background: 'var(--c-grass)', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 'var(--fs-12)', fontWeight: 700, flexShrink: 0 }}>
                        {saving ? '...' : '✓'}
                      </button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ fontWeight: 700, fontSize: 'var(--fs-14)', color: 'var(--c-fg-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {profile?.display_name || 'Trainer'}
                      </div>
                      <button onClick={function() { setEditingName(true); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-fg-3)', fontSize: 12, padding: 0, flexShrink: 0 }}>✎</button>
                    </div>
                  )}
                  <div style={{ fontSize: 'var(--fs-12)', color: 'var(--c-fg-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {session?.user?.email}
                  </div>
                </div>
              </div>
            </div>

            {/* Avatar color picker */}
            <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--c-line)' }}>
              <div style={{ fontSize: 'var(--fs-11)', fontWeight: 600, color: 'var(--c-fg-3)', marginBottom: 8, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Avatar color</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {['#3B82E8','#E8553B','#5EBF5A','#F2C744','#E25CA0','#9B5BB5','#5C5DCB','#C04E3D'].map(function(color) {
                  var active = (profile?.avatar_color || '#3B82E8') === color;
                  return (
                    <button key={color} onClick={async function() {
                      try {
                        await updateProfile(session.user.id, { avatar_color: color });
                        if (onProfileUpdate) onProfileUpdate({ avatar_color: color });
                      } catch(e) {}
                    }} style={{
                      width: 22, height: 22, borderRadius: '50%', background: color, border: active ? '2px solid white' : '2px solid transparent',
                      cursor: 'pointer', outline: active ? '2px solid ' + color : 'none', outlineOffset: 1,
                    }} />
                  );
                })}
              </div>
            </div>

            {/* Sign out */}
            <div style={{ padding: '8px' }}>
              <button onClick={function() { setOpen(false); onSignOut(); }} style={Object.assign({}, btnGhost, { padding: '8px 12px', fontSize: 'var(--fs-13)', textAlign: 'left' })}>
                Sign out
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
